import { geoArea, geoCentroid } from 'd3-geo'
import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  type ProjectionFunction,
  ZoomableGroup,
} from 'react-simple-maps'

import { type MapContent, type MapCountry } from '../../types'
import { DEFAULT_MAP_LABELS, withLabelDefaults } from '../../utils/labels'
import CountryDetails from './CountryDetails'
import MapCallout, { type MapCalloutPosition } from './MapCallout'
import MapControls from './MapControls'
import MapLabels, { type MapLabel } from './MapLabels'
import geo from './geo'
import { getCalloutHorizontalPosition } from './mapCalloutPosition'
import {
  clamp,
  clampMapCenter,
  FULL_MAP_VIEWPORT,
  getMapTranslateExtent,
  getVisibleMapViewport,
  type MapViewport,
} from './mapPanBounds'
import { getCountryMapLabel, pointCountryIds } from './mapPresentation'
import { DEFAULT_MAP_CENTER, worldProjection, MAP_HEIGHT, MAP_WIDTH } from './mapProjection'
import styles from './styles.module.scss'

type FilterOption = {
  value: string
  label: string
}

type MapView = {
  center: [number, number]
  zoom: number
}

type Density = NonNullable<MapCountry['density']>

type MapPathStyle = React.CSSProperties & {
  '--map-fill': string
  '--map-hover-fill': string
}

const DEFAULT_VIEW: MapView = { center: DEFAULT_MAP_CENTER, zoom: 1 }
// Zoom 1 shows the whole world; 9 matches the detail the Eurasia-only map reached at 4.25.
const MAX_ZOOM = 9
const MIN_ZOOM = 1
const ZOOM_STEP = 1.5
const ALL_OFFICES = '__all_offices__'
// Most of the work is in Eurasia, so zooming in from the whole-world view heads there.
const EURASIA_FOCUS: [number, number] = [55, 45]
const CALLOUT_MIN_TOP = 132
const MOBILE_CALLOUT_MIN_TOP = 200
// The mobile country card starts 254px into the map frame (.countryDetails margin-top). The
// callout must stay above it, so a selected country is moved into the band just above the card.
const MOBILE_DETAILS_TOP = 254
const MOBILE_CALLOUT_MAX_TOP = MOBILE_DETAILS_TOP + 6
const MOBILE_SELECTED_ANCHOR_Y = 236
// Figma (mobile map sidebar): a selected country is shown zoomed in above its card.
const MOBILE_SELECTION_ZOOM = 2.25
const DESKTOP_DETAILS_RESERVE = 410
const CALLOUT_CARD_GAP = 16
const POINT_HIT_RADIUS_PX = 22
// Keep in sync with the media query that sets the map's touch-action in styles.module.scss.
const SHORT_TOUCH_SCREEN = '(hover: none) and (orientation: landscape) and (max-height: 500px)'

const colours = {
  empty: '#22203F',
  low: '#F4DACE',
  medium: '#F3AA8B',
  high: '#ED7D4D',
  hover: '#ff9a70',
}

// Paint servers preserve these dark colours in Chromium's forced-dark rendering. Keep light
// fills as solid colours: forced dark can darken a light gradient instead. Samsung needs a
// device retest because its forced-dark implementation differs from desktop Chromium.
const darkMapColours = { empty: colours.empty, border: '#3c3a6e' }
const getCountryPaint = (fill: string) => (fill === colours.empty ? 'var(--map-empty-fill)' : fill)

const getMapPathStyle = (fill: string, hoverFill = colours.hover): MapPathStyle => ({
  '--map-fill': getCountryPaint(fill),
  '--map-hover-fill': getCountryPaint(hoverFill),
})

const hasCoordinates = (coordinates?: number[]): coordinates is [number, number] =>
  Boolean(
    coordinates &&
      coordinates.length === 2 &&
      coordinates.every((coordinate) => Number.isFinite(coordinate))
  )

const hasOffice = (country: MapCountry) =>
  Boolean(country.office?.address?.trim() || country.office?.label?.trim())

const getCountryActivity = (country: MapCountry): Density | null =>
  country.density || (country.cases.length > 0 || hasOffice(country) ? 'low' : null)

const FilterSelect = ({
  label,
  displayValue,
  valueDot,
  showOptionDots = false,
  options,
  selectedValue,
  highlighted = false,
  onSelect,
}: {
  label: string
  displayValue: string
  valueDot?: string
  showOptionDots?: boolean
  options: FilterOption[]
  selectedValue: string | null
  highlighted?: boolean
  onSelect: (value: string) => void
}) => {
  const detailsRef = useRef<HTMLDetailsElement>(null)

  useEffect(() => {
    // Older React typings omit the native details "name" attribute.
    detailsRef.current?.setAttribute('name', 'map-filters')
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!detailsRef.current?.contains(event.target as Node)) {
        detailsRef.current?.removeAttribute('open')
      }
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') detailsRef.current?.removeAttribute('open')
    }

    document.addEventListener('pointerdown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [])

  return (
    <details
      className={`${styles.filter}${highlighted ? ` ${styles.filterActive}` : ''}`}
      ref={detailsRef}
    >
      <summary>
        <span className={styles.filterText}>
          <span className={styles.filterEyebrow}>{label}</span>
          <span className={styles.filterValue}>
            {valueDot ? (
              <span
                className={styles.filterDot}
                style={{ backgroundColor: valueDot }}
                aria-hidden
              />
            ) : null}
            <span>{displayValue}</span>
          </span>
        </span>
      </summary>
      <div className={styles.filterOptions} role="group" aria-label={label}>
        {options.map((option) => {
          return (
            <button
              key={option.value}
              type="button"
              className={styles.filterOption}
              aria-pressed={selectedValue === option.value}
              onClick={() => {
                onSelect(option.value)
                detailsRef.current?.removeAttribute('open')
              }}
            >
              {showOptionDots ? (
                <span
                  className={styles.filterDot}
                  style={{ backgroundColor: colours[option.value as Density] }}
                  aria-hidden
                />
              ) : null}
              <span>{option.label}</span>
            </button>
          )
        })}
      </div>
    </details>
  )
}

const Map = ({ content }: { content: MapContent }) => {
  const mapPaintId = useId()
  const mapFrameRef = useRef<HTMLDivElement>(null)
  const clickedAnchorRef = useRef<DOMPoint | null>(null)
  const countryPaths = useRef<Record<string, SVGPathElement | null>>({})
  const [selectedDensity, setSelectedDensity] = useState<Density | null>(null)
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null)
  const [selectedOffice, setSelectedOffice] = useState<string | null>(null)
  const [selectedCountry, setSelectedCountry] = useState<MapCountry | null>(null)
  const [calloutPosition, setCalloutPosition] = useState<MapCalloutPosition | null>(null)
  const [mapView, setMapView] = useState<MapView>(DEFAULT_VIEW)
  const [visibleViewport, setVisibleViewport] = useState<MapViewport>(FULL_MAP_VIEWPORT)
  // Screen pixels per SVG unit: the phone canvas draws the 1440-wide projection at 840px.
  const [canvasScale, setCanvasScale] = useState(1)
  const [isMobileMap, setIsMobileMap] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [fullscreenError, setFullscreenError] = useState(false)
  // iPhone Safari cannot put an element into full screen; hide the control there.
  const [canFullscreen] = useState(() => Boolean(document.fullscreenEnabled))

  useEffect(() => {
    const syncFullscreen = () => setIsFullscreen(document.fullscreenElement === mapFrameRef.current)
    document.addEventListener('fullscreenchange', syncFullscreen)
    return () => document.removeEventListener('fullscreenchange', syncFullscreen)
  }, [])

  useEffect(() => {
    const frame = mapFrameRef.current
    const canvas = frame?.querySelector<SVGSVGElement>('svg.rsm-svg')
    if (!frame || !canvas) return

    const syncViewport = () => {
      const scale = canvas.getBoundingClientRect().width / MAP_WIDTH
      if (scale > 0)
        setCanvasScale((current) => (Math.abs(current - scale) < 0.01 ? current : scale))
      const viewport = getVisibleMapViewport(frame, canvas)
      if (!viewport) return
      setVisibleViewport((current) =>
        (Object.keys(viewport) as Array<keyof MapViewport>).every(
          (key) => Math.abs(current[key] - viewport[key]) < 0.5
        )
          ? current
          : viewport
      )
    }

    const observer = new ResizeObserver(syncViewport)
    observer.observe(frame)
    observer.observe(canvas)
    window.addEventListener('resize', syncViewport)
    syncViewport()

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', syncViewport)
    }
  }, [])

  // d3-zoom leaves the wheel event alone once the zoom limit is reached, so the browser would
  // scroll or page-zoom instead. Over the map, modifier-wheel and trackpad pinch always belong to it.
  useEffect(() => {
    const frame = mapFrameRef.current
    if (!frame) return

    const captureZoomGesture = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) event.preventDefault()
    }
    const preventSafariPageZoom = (event: Event) => event.preventDefault()
    // iOS Safari still zooms the page on a pinch that starts over the map's overlays (country card,
    // callout) unless the multi-touch move itself is cancelled. d3-zoom still receives the events.
    const preventPinchPageZoom = (event: TouchEvent) => {
      if (event.touches.length > 1) event.preventDefault()
    }

    frame.addEventListener('wheel', captureZoomGesture, { passive: false })
    frame.addEventListener('gesturestart', preventSafariPageZoom)
    frame.addEventListener('gesturechange', preventSafariPageZoom)
    frame.addEventListener('touchmove', preventPinchPageZoom, { passive: false })
    return () => {
      frame.removeEventListener('wheel', captureZoomGesture)
      frame.removeEventListener('touchmove', preventPinchPageZoom)
      frame.removeEventListener('gesturestart', preventSafariPageZoom)
      frame.removeEventListener('gesturechange', preventSafariPageZoom)
    }
  }, [])

  const toggleFullscreen = async () => {
    const frame = mapFrameRef.current
    if (!frame) return

    try {
      if (document.fullscreenElement === frame) {
        await document.exitFullscreen()
      } else {
        await frame.requestFullscreen()
      }
      setFullscreenError(false)
    } catch {
      setFullscreenError(true)
    }
  }

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)')
    const update = () => {
      setIsMobileMap(mediaQuery.matches)
      if (mediaQuery.matches) setMapView(DEFAULT_VIEW)
    }
    update()
    mediaQuery.addEventListener('change', update)
    return () => mediaQuery.removeEventListener('change', update)
  }, [])

  const allowMapPan = useCallback((event: SVGElement) => {
    const pointerEvent = event as unknown as MouseEvent
    if (pointerEvent.type === 'wheel') return pointerEvent.ctrlKey || pointerEvent.metaKey
    // A phone held sideways shows little more than the map, so one finger scrolls the page
    // and two fingers move or pinch the map (the zoom buttons still work).
    const touchEvent = event as unknown as TouchEvent
    if (touchEvent.type === 'touchstart' && touchEvent.touches.length < 2) {
      if (window.matchMedia(SHORT_TOUCH_SCREEN).matches) return false
    }
    return !pointerEvent.ctrlKey && !pointerEvent.button
  }, [])

  const getSafeCalloutPosition = useCallback(
    (
      target: Element,
      pointer?: { clientX: number; clientY: number },
      reserveDetails = false
    ): MapCalloutPosition | null => {
      const frame = mapFrameRef.current
      if (!frame) return null

      const frameRect = frame.getBoundingClientRect()
      const targetRect = target.getBoundingClientRect()
      let detailsReserve = 24
      if (!isMobileMap && reserveDetails) {
        const details = frame.querySelector<HTMLElement>('[data-testid="country-details"]')
        detailsReserve = details
          ? frame.clientWidth - details.offsetLeft + CALLOUT_CARD_GAP
          : DESKTOP_DETAILS_RESERVE
      }
      const clientX = pointer?.clientX ?? targetRect.left + targetRect.width / 2
      const clientY = pointer?.clientY ?? targetRect.top + targetRect.height / 2
      const anchorX = clientX - frameRect.left
      const anchorY = clientY - frameRect.top

      // A click can only reach the map outside the details card, including the narrow strip beside it.
      if (anchorX < 0 || anchorX > frameRect.width || anchorY < 0 || anchorY > frameRect.height) {
        return null
      }

      const { x, arrowOffset } = getCalloutHorizontalPosition(
        anchorX,
        frameRect.width,
        detailsReserve,
        isMobileMap
      )
      return {
        x,
        y: clamp(
          anchorY,
          isMobileMap ? MOBILE_CALLOUT_MIN_TOP : CALLOUT_MIN_TOP,
          isMobileMap && !isFullscreen ? MOBILE_CALLOUT_MAX_TOP : frameRect.height - 24
        ),
        arrowOffset,
      }
    },
    [isMobileMap, isFullscreen]
  )

  const countryDataById = useMemo(
    () =>
      content.countries.reduce<Record<string, MapCountry>>((countries, country) => {
        countries[country.id] = country
        return countries
      }, {}),
    [content.countries]
  )

  const subjectOptions = useMemo<FilterOption[]>(
    () =>
      Array.from(new Set(content.countries.flatMap((country) => country.subjects)))
        .filter(Boolean)
        .map((subject) => ({ value: subject, label: subject })),
    [content.countries]
  )
  const officeOptions = useMemo<FilterOption[]>(
    () =>
      // countryDataById keeps one entry per country, so CMS duplicates are listed once.
      Object.values(countryDataById)
        .filter(hasOffice)
        .map((country) => ({ value: country.id, label: country.name })),
    [countryDataById]
  )
  const hasActiveFilters = Boolean(selectedDensity || selectedSubject || selectedOffice)

  // Keeps the middle of the visible part of the canvas in place. On mobile the canvas is wider
  // than the screen and offset, so its own centre is not what the user is looking at.
  const getZoomPivotCenter = (
    center: [number, number],
    zoom: number,
    nextZoom: number
  ): [number, number] => {
    const projectedCenter = worldProjection(center)
    if (!projectedCenter || !worldProjection.invert) return center

    const offsetX = (visibleViewport.left + visibleViewport.right) / 2 - MAP_WIDTH / 2
    const offsetY = (visibleViewport.top + visibleViewport.bottom) / 2 - MAP_HEIGHT / 2
    const pivot =
      (zoom <= MIN_ZOOM && worldProjection(EURASIA_FOCUS)) ||
      ([projectedCenter[0] + offsetX / zoom, projectedCenter[1] + offsetY / zoom] as [
        number,
        number,
      ])
    return (
      worldProjection.invert([pivot[0] - offsetX / nextZoom, pivot[1] - offsetY / nextZoom]) ||
      center
    )
  }

  const changeZoom = (factor: number) => {
    setMapView(({ center, zoom }) => {
      const nextZoom = clamp(zoom * factor, MIN_ZOOM, MAX_ZOOM)
      const selectedPoint =
        isMobileMap && !isFullscreen && hasCoordinates(selectedCountry?.labelCoordinates)
          ? worldProjection(selectedCountry.labelCoordinates)
          : null
      const mobileSelectionCenter =
        selectedPoint && getMobileCardBandCenter(selectedPoint, nextZoom)
      if (mobileSelectionCenter) return { center: mobileSelectionCenter, zoom: nextZoom }

      const preferredCenter =
        factor > 1 && hasCoordinates(selectedCountry?.labelCoordinates)
          ? selectedCountry.labelCoordinates
          : getZoomPivotCenter(center, zoom, nextZoom)
      return {
        center: clampMapCenter(preferredCenter, nextZoom, visibleViewport),
        zoom: nextZoom,
      }
    })
  }

  const resetMapView = () => {
    setMapView(DEFAULT_VIEW)
  }

  // Only one filter is active at a time: choosing a value in any group replaces the previous
  // filter; choosing the active value again clears it.
  const toggleFilter = (group: 'density' | 'office' | 'subject', value: string) => {
    const currentValue = {
      density: selectedDensity,
      office: selectedOffice,
      subject: selectedSubject,
    }[group]
    const nextValue = currentValue === value ? null : value
    setSelectedDensity(group === 'density' ? (nextValue as Density | null) : null)
    setSelectedOffice(group === 'office' ? nextValue : null)
    setSelectedSubject(group === 'subject' ? nextValue : null)
    setCalloutPosition(null)
    setMapView(DEFAULT_VIEW)
    clickedAnchorRef.current = null

    // A single office points at one country, so open its card as a click would.
    const officeCountry =
      group === 'office' && nextValue && nextValue !== ALL_OFFICES
        ? countryDataById[nextValue]
        : undefined
    setSelectedCountry(officeCountry || null)
  }

  const countryMatchesFilters = (country?: MapCountry) => {
    if (!country) return false
    if (selectedDensity) return selectedDensity === getCountryActivity(country)
    if (selectedSubject) return country.subjects.includes(selectedSubject)
    if (selectedOffice === ALL_OFFICES) return hasOffice(country)
    if (selectedOffice) return selectedOffice === country.id
    return true
  }

  const getCountryFill = (country?: MapCountry) => {
    if (!country || (hasActiveFilters && !countryMatchesFilters(country))) {
      return colours.empty
    }

    const activity = getCountryActivity(country)
    return activity ? colours[activity] : colours.empty
  }

  const selectCountry = (
    country: MapCountry,
    target: Element,
    pointer?: { clientX: number; clientY: number },
    coordinates?: [number, number]
  ) => {
    const zoomGroup = target.closest<SVGGElement>('.rsm-zoomable-group')
    const matrix = zoomGroup?.getScreenCTM()
    // Keep the clicked point in map coordinates so later positioning does not replace it
    // with the country's label anchor, and panning/zooming can still move it with the map.
    clickedAnchorRef.current =
      mapView.zoom > MIN_ZOOM && pointer && matrix
        ? new DOMPoint(pointer.clientX, pointer.clientY).matrixTransform(matrix.inverse())
        : null
    const anchorCoordinates = hasCoordinates(country.labelCoordinates)
      ? country.labelCoordinates
      : coordinates
    const projectedAnchor = clickedAnchorRef.current
      ? ([clickedAnchorRef.current.x, clickedAnchorRef.current.y] as [number, number])
      : anchorCoordinates && worldProjection(anchorCoordinates)
    if (isMobileMap && !isFullscreen && projectedAnchor) {
      setMapView(({ center, zoom }) => {
        const nextZoom = Math.max(zoom, MOBILE_SELECTION_ZOOM)
        return {
          zoom: nextZoom,
          center: getMobileCardBandCenter(projectedAnchor, nextZoom) || center,
        }
      })
    }
    setSelectedCountry({
      ...country,
      labelCoordinates: hasCoordinates(country.labelCoordinates)
        ? country.labelCoordinates
        : coordinates,
    })
  }

  // Centre that shows a projected point in the visible band above the mobile country card.
  const getMobileCardBandCenter = (
    projectedPoint: [number, number],
    zoom: number
  ): [number, number] | null => {
    const frame = mapFrameRef.current
    const matrix = frame?.querySelector<SVGSVGElement>('svg.rsm-svg')?.getScreenCTM()
    if (!frame || !matrix || !worldProjection.invert) return null

    const frameRect = frame.getBoundingClientRect()
    const target = new DOMPoint(
      frameRect.left + frameRect.width / 2,
      frameRect.top + MOBILE_SELECTED_ANCHOR_Y
    ).matrixTransform(matrix.inverse())
    const center = worldProjection.invert([
      projectedPoint[0] - (target.x - MAP_WIDTH / 2) / zoom,
      projectedPoint[1] - (target.y - MAP_HEIGHT / 2) / zoom,
    ])
    return center && clampMapCenter(center, zoom, visibleViewport)
  }

  const closeCallout = () => {
    setSelectedCountry(null)
    setCalloutPosition(null)
  }

  useEffect(() => {
    if (!selectedCountry) return
    const closeOnEscape = (event: KeyboardEvent) => {
      // An open filter dropdown handles Escape itself.
      if (event.key !== 'Escape' || document.querySelector('details[name="map-filters"][open]'))
        return
      setSelectedCountry(null)
      setCalloutPosition(null)
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [selectedCountry])

  const clearFilters = () => {
    setSelectedDensity(null)
    setSelectedSubject(null)
    setSelectedOffice(null)
  }

  const resetMap = () => {
    clearFilters()
    setSelectedCountry(null)
    setCalloutPosition(null)
    setMapView(DEFAULT_VIEW)
  }

  useLayoutEffect(() => {
    if (!selectedCountry || !mapFrameRef.current) return

    const frame = mapFrameRef.current
    const updatePosition = () => {
      const target = frame.querySelector('[data-selected-anchor]')
      if (!target) return false

      const matrix = frame.querySelector<SVGGElement>('.rsm-zoomable-group')?.getScreenCTM()
      const clickedPoint = matrix && clickedAnchorRef.current?.matrixTransform(matrix)
      const pointer = clickedPoint
        ? { clientX: clickedPoint.x, clientY: clickedPoint.y }
        : undefined
      setCalloutPosition(getSafeCalloutPosition(target, pointer, true))
      return true
    }
    const observer = new MutationObserver(() => {
      if (updatePosition()) observer.disconnect()
    })
    observer.observe(frame, { childList: true, subtree: true })
    // react-simple-maps applies a new centre/zoom after this effect runs, so follow the group's
    // transform; otherwise a programmatic pan (e.g. the mobile selection zoom) leaves a stale popup.
    const transformObserver = new MutationObserver(() => updatePosition())
    const zoomGroup = frame.querySelector('.rsm-zoomable-group')
    if (zoomGroup) {
      transformObserver.observe(zoomGroup, { attributes: true, attributeFilter: ['transform'] })
    }
    const resizeObserver = new ResizeObserver(updatePosition)
    resizeObserver.observe(frame)
    window.addEventListener('resize', updatePosition)
    updatePosition()
    let secondFrame = 0
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        if (updatePosition()) observer.disconnect()
      })
    })

    return () => {
      observer.disconnect()
      transformObserver.disconnect()
      resizeObserver.disconnect()
      window.removeEventListener('resize', updatePosition)
      window.cancelAnimationFrame(firstFrame)
      window.cancelAnimationFrame(secondFrame)
    }
  }, [getSafeCalloutPosition, mapView.center[0], mapView.center[1], mapView.zoom, selectedCountry])

  const labels = withLabelDefaults(DEFAULT_MAP_LABELS, content.labels)
  const densityOptions: FilterOption[] = [
    { value: 'high', label: labels.densityHigh },
    { value: 'medium', label: labels.densityMedium },
    { value: 'low', label: labels.densityLow },
  ]
  const officeFilterOptions: FilterOption[] = [
    { value: ALL_OFFICES, label: labels.allOffices },
    ...officeOptions,
  ]
  const descriptionParagraphs = content.description.split(/\n\s*\n/).filter(Boolean)
  const activeCalloutCountry = selectedCountry
  // The boxes show the active filter only; a clicked country does not count as a filter.
  const densityDisplay =
    densityOptions.find((option) => option.value === selectedDensity)?.label || labels.allActivity
  const densitySwatch = selectedDensity ? colours[selectedDensity] : undefined
  const officeDisplay =
    officeFilterOptions.find((option) => option.value === selectedOffice)?.label ||
    labels.allOffices
  const subjectDisplay = selectedSubject || labels.allSubjects

  return (
    <section className={styles.section} aria-labelledby="cross-border-intelligence-title">
      <div className={styles.content} data-testid="map-content">
        <div className={styles.heading}>
          <h2 id="cross-border-intelligence-title">{content.title}</h2>
          <div className={styles.description}>
            {descriptionParagraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>

        <div className={styles.filters} aria-label="Map filters">
          <FilterSelect
            label={labels.presence}
            displayValue={densityDisplay}
            valueDot={densitySwatch}
            showOptionDots
            options={densityOptions}
            selectedValue={selectedDensity}
            highlighted={Boolean(selectedDensity)}
            onSelect={(value) => toggleFilter('density', value)}
          />
          <FilterSelect
            label={labels.officeLocation}
            displayValue={officeDisplay}
            options={officeFilterOptions}
            selectedValue={selectedOffice}
            highlighted={Boolean(selectedOffice)}
            onSelect={(value) => toggleFilter('office', value)}
          />
          <FilterSelect
            label={labels.subjects}
            displayValue={subjectDisplay}
            options={subjectOptions}
            selectedValue={selectedSubject}
            highlighted={Boolean(selectedSubject)}
            onSelect={(value) => toggleFilter('subject', value)}
          />
          {hasActiveFilters ? (
            <button type="button" className={styles.clearFilters} onClick={resetMap}>
              <span aria-hidden>×</span> {labels.clearFilters}
            </button>
          ) : null}
        </div>
      </div>

      <div
        className={styles.mapFrame}
        data-testid="eurasia-map"
        data-map-zoom={mapView.zoom.toFixed(2)}
        data-map-zoomed={mapView.zoom > MIN_ZOOM}
        ref={mapFrameRef}
      >
        <MapControls
          canZoomIn={mapView.zoom < MAX_ZOOM}
          canZoomOut={mapView.zoom > MIN_ZOOM}
          canFullscreen={canFullscreen}
          isFullscreen={isFullscreen}
          onZoomIn={() => changeZoom(ZOOM_STEP)}
          onZoomOut={() => changeZoom(1 / ZOOM_STEP)}
          onReset={resetMapView}
          onToggleFullscreen={() => {
            void toggleFullscreen()
          }}
        />
        {fullscreenError ? (
          <p className={styles.fullscreenError} role="status">
            Full screen is unavailable in this browser.
          </p>
        ) : null}

        <ComposableMap
          className={styles.mapCanvas}
          style={
            {
              '--map-empty-fill': `url(#${mapPaintId}-empty)`,
              '--map-border': `url(#${mapPaintId}-border)`,
            } as React.CSSProperties
          }
          width={MAP_WIDTH}
          height={MAP_HEIGHT}
          projection={worldProjection as unknown as ProjectionFunction}
        >
          <defs>
            {Object.entries(darkMapColours).map(([name, colour]) => (
              <linearGradient
                key={name}
                id={`${mapPaintId}-${name}`}
                gradientUnits="userSpaceOnUse"
                x1="0"
                y1="0"
                x2="1"
                y2="0"
              >
                <stop offset="0" stopColor={colour} />
                <stop offset="1" stopColor={colour} />
              </linearGradient>
            ))}
            <pattern
              id="selected-country-hatch"
              width={8 / mapView.zoom}
              height={8 / mapView.zoom}
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <line
                x1="0"
                y1="0"
                x2="0"
                y2={8 / mapView.zoom}
                stroke="#191736"
                strokeWidth={2 / mapView.zoom}
              />
            </pattern>
          </defs>
          <ZoomableGroup
            center={mapView.center}
            zoom={mapView.zoom}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            translateExtent={getMapTranslateExtent(visibleViewport, mapView.zoom)}
            filterZoomEvent={allowMapPan}
            onMoveEnd={({ coordinates, zoom }) => {
              setMapView({ center: coordinates, zoom })
            }}
          >
            <Geographies geography={geo}>
              {({ geographies }) => {
                const geographyItems = geographies.map((geography) => {
                  const countryId = String(geography.id)
                  const country = countryDataById[countryId]
                  const fill = getCountryFill(country)
                  const isInteractive =
                    Boolean(country) && (!hasActiveFilters || countryMatchesFilters(country))
                  const isSelected = selectedCountry?.id === countryId
                  const label = getCountryMapLabel(countryId, country)
                  const isVisibleForFilters =
                    !hasActiveFilters || Boolean(country && countryMatchesFilters(country))
                  const shouldShowLabel = Boolean(
                    label && mapView.zoom >= label.minZoom && isVisibleForFilters
                  )
                  const coordinates = hasCoordinates(country?.labelCoordinates)
                    ? country.labelCoordinates
                    : (geoCentroid(geography) as [number, number])
                  // A small-country dot only marks a country with map data; one with no data (e.g.
                  // Taiwan) would look clickable but do nothing. A country with its own office marker
                  // (Hong Kong) needs no second dot.
                  const shouldShowPoint = Boolean(
                    country &&
                      !hasCoordinates(country.coordinates) &&
                      pointCountryIds.has(countryId) &&
                      !shouldShowLabel &&
                      isVisibleForFilters
                  )

                  return {
                    geography,
                    countryId,
                    country,
                    fill,
                    isInteractive,
                    isSelected,
                    label,
                    shouldShowLabel,
                    shouldShowPoint,
                    coordinates,
                  }
                })
                const mapLabels: MapLabel[] = geographyItems.flatMap((item) => {
                  if (!item.shouldShowLabel || !item.label) return []
                  return [
                    {
                      id: item.countryId,
                      coordinates: item.coordinates,
                      presentation: item.label,
                      light: Boolean(item.label.light || item.fill === colours.empty),
                      priority:
                        (item.isSelected ? 100 : 0) +
                        (item.label.size === 'large' ? 10 : 0) +
                        geoArea(item.geography),
                    },
                  ]
                })

                return (
                  <>
                    {geographyItems.map(
                      ({
                        geography,
                        countryId,
                        country,
                        fill,
                        isInteractive,
                        isSelected,
                        shouldShowPoint,
                        coordinates,
                      }) => {
                        return (
                          <React.Fragment key={geography.rsmKey}>
                            <Geography
                              geography={geography}
                              ref={(node) => {
                                countryPaths.current[countryId] = node
                              }}
                              className={`${styles.country}${isInteractive ? ` ${styles.countryInteractive}` : ''}${isSelected ? ` ${styles.countrySelected}` : ''}`}
                              data-country-id={countryId}
                              role={isInteractive ? 'button' : undefined}
                              tabIndex={isInteractive ? 0 : undefined}
                              aria-label={
                                isInteractive && country
                                  ? `Show information for ${country.name}`
                                  : undefined
                              }
                              aria-pressed={isInteractive ? isSelected : undefined}
                              onClick={
                                isInteractive && country
                                  ? (event) =>
                                      selectCountry(
                                        country,
                                        event.currentTarget,
                                        {
                                          clientX: event.clientX,
                                          clientY: event.clientY,
                                        },
                                        coordinates
                                      )
                                  : undefined
                              }
                              onKeyDown={
                                isInteractive && country
                                  ? (event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault()
                                        selectCountry(
                                          country,
                                          event.currentTarget,
                                          undefined,
                                          coordinates
                                        )
                                      }
                                    }
                                  : undefined
                              }
                              style={{
                                default: getMapPathStyle(fill),
                                hover: getMapPathStyle(
                                  isInteractive && !isSelected ? colours.hover : fill
                                ),
                                pressed: getMapPathStyle(fill, fill),
                              }}
                            />

                            {isSelected ? (
                              <Geography
                                geography={geography}
                                fill="url(#selected-country-hatch)"
                                stroke="none"
                                className={styles.countryHatch}
                                tabIndex={-1}
                                aria-hidden
                              />
                            ) : null}

                            {isSelected ? (
                              <Marker coordinates={coordinates} data-selected-anchor aria-hidden>
                                <circle r={1} fill="transparent" />
                              </Marker>
                            ) : null}

                            {shouldShowPoint ? (
                              <Marker
                                coordinates={coordinates}
                                className={styles.smallCountryPoint}
                                aria-hidden
                              >
                                <g transform={`scale(${1 / mapView.zoom})`}>
                                  <circle r={3.5} />
                                </g>
                              </Marker>
                            ) : null}
                          </React.Fragment>
                        )
                      }
                    )}
                    <MapLabels
                      labels={mapLabels}
                      zoom={mapView.zoom}
                      countryPaths={countryPaths}
                      frameRef={mapFrameRef}
                    />
                  </>
                )
              }}
            </Geographies>

            {content.countries
              .filter(
                (country): country is MapCountry & { coordinates: [number, number] } =>
                  hasCoordinates(country.coordinates) &&
                  (!hasActiveFilters || countryMatchesFilters(country))
              )
              .map((country) => {
                const isSelected = selectedCountry?.id === country.id

                return (
                  <Marker
                    key={country.id}
                    coordinates={country.coordinates}
                    className={`${styles.pointCountry}${isSelected ? ` ${styles.pointCountrySelected}` : ''}`}
                    data-country-id={country.id}
                    data-selected-anchor={isSelected ? true : undefined}
                    role="button"
                    tabIndex={0}
                    aria-label={`Show information for ${country.name}`}
                    aria-pressed={isSelected}
                    onClick={(event) =>
                      selectCountry(
                        country,
                        event.currentTarget,
                        {
                          clientX: event.clientX,
                          clientY: event.clientY,
                        },
                        country.coordinates
                      )
                    }
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        selectCountry(country, event.currentTarget, undefined, country.coordinates)
                      }
                    }}
                  >
                    <g transform={`scale(${1 / mapView.zoom})`}>
                      {/* Invisible 44px touch target: the visible dot alone is too small to tap. */}
                      <circle
                        r={POINT_HIT_RADIUS_PX / canvasScale}
                        className={styles.pointCountryHitArea}
                      />
                      <circle r={5} />
                    </g>
                  </Marker>
                )
              })}
          </ZoomableGroup>
        </ComposableMap>

        {activeCalloutCountry && calloutPosition ? (
          <MapCallout
            country={activeCalloutCountry}
            position={calloutPosition}
            onClose={closeCallout}
            projectsInLabel={labels.projectsIn}
          />
        ) : null}

        {selectedCountry ? (
          <CountryDetails
            country={selectedCountry}
            allCasesLabel={content.allCasesLabel}
            allCasesUrl={content.allCasesUrl}
            onClose={closeCallout}
            labels={labels}
          />
        ) : null}
      </div>
    </section>
  )
}

export default Map
